/**
 * Lift2Fit — coaching workspace: Team (`#/crm/team`).
 *
 * The gym's one place to answer "who did we invite, who joined, and how are they
 * doing". It exists because the team feature had two half-surfaces: management
 * in Settings and a read-only summary panel on Clients. This page is both, in
 * one list, and Settings now points here instead of duplicating it.
 *
 * The product rules it renders, and does not invent:
 *   * Only a plan whose internal-coach allowance is above zero has a team at all
 *     (0033). A Free or Coach account gets no invite form — the page says which
 *     plans include teams rather than showing a button that would be refused.
 *   * Adding a coach IS the invitation. The email is the binding: a coach who
 *     already has an account joins with it, a coach who is new signs up with it
 *     and then accepts. The optional NICKNAME is the gym's own short name for
 *     them while the invitation is pending (0033); once they join, every surface
 *     uses their account's own name.
 *   * The counts beside each coach come from the API, one row per coach, so this
 *     page cannot disagree with the client pages about who manages whom.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { api, type InternalCoach, type InternalCoachInvite } from '@/lib/api';
import { Button } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, CheckIcon, PlusIcon, TimerIcon, TrashIcon } from '@/lib/icons';
import { EmptyState, Field, LoadingPanel, Panel, ScreenHeader, StatusChip } from '../components';
import { useAsync } from '../hooks';
import { Link } from '../router';
import { useSession } from '@/lib/useSession';
import { CRM_EYEBROW, CrmMessage, formatDateShort } from './shared';

/** Absolute link for an invitation, built from the relative `shareUrl`. */
function shareLink(invite: InternalCoachInvite): string {
  if (typeof window === 'undefined') return invite.shareUrl;
  return `${window.location.origin}${window.location.pathname}#${invite.shareUrl}`;
}

/** "invited 3 days ago · expires 24 Oct" — relative first, because that is the urgency. */
function inviteTiming(invite: InternalCoachInvite): string {
  const created = Date.parse(invite.createdAt);
  const expires = Date.parse(invite.expiresAt);
  const parts: string[] = [];
  if (Number.isFinite(created)) {
    const days = Math.floor((Date.now() - created) / 86_400_000);
    parts.push(days <= 0 ? 'invited today' : `invited ${days} day${days === 1 ? '' : 's'} ago`);
  }
  if (Number.isFinite(expires)) parts.push(`expires ${formatDateShort(invite.expiresAt)}`);
  return parts.join(' · ');
}

function coachStats(coach: InternalCoach): string {
  const inactive = coach.inactiveClients;
  const clients =
    coach.clientsManaged === 0
      ? 'no clients assigned yet'
      : `${coach.clientsManaged} client${coach.clientsManaged === 1 ? '' : 's'}` +
        (inactive > 0 ? ` · ${inactive} inactive` : ' · all active');
  const parts = [clients, `${coach.sessionsLast7} session${coach.sessionsLast7 === 1 ? '' : 's'} this week`];
  if (coach.adherencePct !== null) parts.push(`${Math.round(coach.adherencePct)}% adherence`);
  if (coach.unreadChats > 0) parts.push(`${coach.unreadChats} unread`);
  return parts.join(' · ');
}

export function CrmTeamScreen() {
  const session = useSession();
  const team = useAsync(() => api.internalCoaches(), []);
  const [email, setEmail] = useState('');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [tone, setTone] = useState<'info' | 'success' | 'danger'>('info');

  const user = session.user;
  const isInternalCoach = user?.internalCoachOf != null;
  const limit = team.data?.limit ?? 0;
  const hasTeam = team.data !== null && (limit === null || limit > 0);
  const coaches = (team.data?.data ?? []).filter((coach) => coach.status === 'active');
  const pending = (team.data?.invites ?? []).filter(
    (invite) => invite.status === 'pending' && !invite.expired,
  );

  // A coach may type this URL; the answer is a plain panel, never a form that
  // the API would refuse.
  useEffect(() => {
    if (team.data && !hasTeam) setTone('info');
  }, [team.data, hasTeam]);

  const invite = async (event: FormEvent) => {
    event.preventDefault();
    const address = email.trim();
    if (address === '') return;
    setBusy('invite');
    setTone('info');
    setMessage('Sending the invitation…');
    const result = await api.inviteInternalCoach(address, label.trim() === '' ? null : label.trim());
    setBusy(null);
    if (result.problem || !result.data) {
      setTone('danger');
      setMessage(result.problem?.detail ?? 'That invitation did not send.');
      return;
    }
    setEmail('');
    setLabel('');
    setTone('success');
    setMessage(
      result.data.label
        ? `Invited ${result.data.label} (${result.data.email}). They join with that email.`
        : `Invited ${result.data.email}. They join with that email.`,
    );
    team.reload();
  };

  const withdraw = async (inviteId: string, who: string) => {
    setBusy(inviteId);
    const result = await api.revokeInternalCoachInvite(inviteId);
    setBusy(null);
    if (result.problem) {
      setTone('danger');
      setMessage(result.problem.detail ?? 'That invitation was not withdrawn.');
      return;
    }
    setTone('success');
    setMessage(`Withdrew the invitation for ${who}.`);
    team.reload();
  };

  const remove = async (coach: InternalCoach) => {
    setBusy(coach.coachId);
    const result = await api.removeInternalCoach(coach.coachId);
    setBusy(null);
    if (result.problem) {
      setTone('danger');
      setMessage(result.problem.detail ?? 'That coach was not removed.');
      return;
    }
    setTone('success');
    setMessage(
      `Removed ${coach.displayName}. Their clients went back to you — their programmes and history did not move.`,
    );
    team.reload();
  };

  const copy = async (invite: InternalCoachInvite) => {
    const link = shareLink(invite);
    try {
      await navigator.clipboard.writeText(link);
      setTone('success');
      setMessage('Invitation link copied — send it to the coach if email does not reach them.');
    } catch {
      setTone('info');
      setMessage(`Copy this link yourself: ${link}`);
    }
  };

  return (
    <div className="flex flex-col gap-6" data-testid="crm-team">
      <ScreenHeader
        eyebrow={CRM_EYEBROW}
        title="Team"
        badge={
          team.data ? (
            <StatusChip tone={coaches.length ? 'info' : 'neutral'}>
              {team.data.used}
              {limit === null ? '' : ` of ${limit}`} coach{limit === 1 ? '' : 'es'}
            </StatusChip>
          ) : undefined
        }
        lead="The coaches who work here. Inviting one is the whole step: a coach who already has an account joins with it, and a coach who is new signs up with the same email and accepts. Either way they manage only the clients you assign, and never see your payments or revenue."
      />

      {team.loading && !team.data ? (
        <LoadingPanel label="Loading your team" rows={3} />
      ) : team.error && !team.data ? (
        <EmptyState
          title="We could not read your team"
          body={team.error}
          icon={<AlertIcon className="h-8 w-8 text-accent" />}
        />
      ) : isInternalCoach ? (
        <Panel testId="crm-team-internal" title="You work within a gym’s team">
          <p className="text-[14px] text-ink-muted">
            {user?.internalCoachOf?.displayName ?? 'Your gym'} manages the team and assigns your clients.
            Everything you can act on is on the Clients page.
          </p>
          <Link
            to="/crm/clients"
            data-testid="crm-team-internal-clients"
            className="flex items-center justify-between gap-3 border border-hairline px-4 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-ink hover:border-accent hover:text-accent"
          >
            Your clients <ArrowRightIcon />
          </Link>
        </Panel>
      ) : !hasTeam ? (
        <Panel testId="crm-team-upgrade" title="Teams are a Gyms feature">
          <p className="text-[14px] text-ink-muted">
            {team.data?.plan === 'coach'
              ? 'Your Coach plan is for managing your own clients. Internal coaches come with Gyms: £49 a month for up to 20 clients and 10 coaches, or Gyms Plus at £99 for 100 clients and 20 coaches.'
              : 'Gyms can add coaches who manage the clients they assign. Gyms is £49 a month for up to 20 clients and 10 coaches; Gyms Plus is £99 for 100 clients and 20 coaches.'}
          </p>
          <Link
            to="/for-coaches#crm-pricing"
            data-testid="crm-team-upgrade-link"
            className="flex items-center justify-between gap-3 border border-hairline px-4 py-3 text-[13px] font-bold uppercase tracking-[0.06em] text-ink hover:border-accent hover:text-accent"
          >
            See the plans <ArrowRightIcon />
          </Link>
        </Panel>
      ) : (
        <>
          <Panel
            testId="crm-team-invite"
            title="Invite a coach"
            subtitle="Use the email they sign in with. The nickname is only for recognising them here until they join — after that every surface uses their own name."
            bodyClassName="gap-3"
          >
            <form onSubmit={invite} className="flex flex-col gap-3" data-testid="crm-team-invite-form">
              <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
                <Field label="Email">
                  <input
                    type="email"
                    required
                    data-testid="crm-team-invite-email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    placeholder="coach@example.com"
                    className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                  />
                </Field>
                <Field label="Nickname (optional)">
                  <input
                    type="text"
                    maxLength={60}
                    data-testid="crm-team-invite-label"
                    value={label}
                    onChange={(event) => setLabel(event.target.value)}
                    placeholder="Sam S — mornings"
                    className="h-11 border border-hairline bg-page px-3 text-[15px] text-ink"
                  />
                </Field>
              </div>
              <div>
                <Button type="submit" variant="secondary" size="sm" disabled={busy === 'invite'} data-testid="crm-team-invite-submit">
                  <PlusIcon /> Send invitation
                </Button>
              </div>
            </form>
            <CrmMessage message={message} tone={tone} testId="crm-team-message" />
          </Panel>

          <Panel
            testId="crm-team-pending"
            title="Invitations waiting"
            subtitle="They have not joined yet. The nickname is how you will recognise them here; the email is what binds the account."
            bodyClassName="gap-3"
          >
            {pending.length === 0 ? (
              <p className="text-[13px] text-ink-muted" data-testid="crm-team-pending-empty">
                No invitations waiting.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-hairline" data-testid="crm-team-pending-list">
                {pending.map((entry) => (
                  <li
                    key={entry.id}
                    data-testid={`crm-team-pending-${entry.id}`}
                    className="flex flex-wrap items-center justify-between gap-3 py-3"
                  >
                    <span className="flex min-w-0 flex-col gap-0.5">
                      <span className="text-[14px] font-bold text-ink">
                        {entry.label ?? entry.email}
                        {entry.label ? (
                          <span className="font-normal text-ink-muted"> · {entry.email}</span>
                        ) : null}
                      </span>
                      <span className="text-[12px] text-ink-muted">{inviteTiming(entry)}</span>
                    </span>
                    <span className="flex flex-wrap items-center gap-2">
                      <Button
                        size="sm"
                        variant="ghost"
                        data-testid={`crm-team-pending-copy-${entry.id}`}
                        onClick={() => void copy(entry)}
                      >
                        Copy link
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        disabled={busy === entry.id}
                        data-testid={`crm-team-pending-withdraw-${entry.id}`}
                        onClick={() => void withdraw(entry.id, entry.label ?? entry.email)}
                      >
                        Withdraw
                      </Button>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Panel>

          <Panel
            testId="crm-team-coaches"
            title="Your coaches"
            subtitle="Everyone who has joined, with what they are carrying. Counts are live from the clients assigned to each of them."
            bodyClassName="gap-3"
          >
            {coaches.length === 0 ? (
              <p className="text-[13px] text-ink-muted" data-testid="crm-team-coaches-empty">
                Nobody has joined yet. Send an invitation above.
              </p>
            ) : (
              <ul className="flex flex-col divide-y divide-hairline" data-testid="crm-team-coaches-list">
                {coaches.map((coach) => (
                  <li
                    key={coach.coachId}
                    data-testid={`crm-team-coach-${coach.coachId}`}
                    className="flex flex-wrap items-start justify-between gap-3 py-3"
                  >
                    <span className="flex min-w-0 flex-col gap-1">
                      <span className="flex flex-wrap items-center gap-2 text-[14px] font-bold text-ink">
                        {coach.displayName}
                        {coach.clientsManaged === 0 ? (
                          <StatusChip tone="neutral" icon={<TimerIcon />}>
                            No clients yet
                          </StatusChip>
                        ) : coach.inactiveClients >= coach.clientsManaged ? (
                          <StatusChip tone="warning" icon={<AlertIcon />}>
                            All inactive
                          </StatusChip>
                        ) : (
                          <StatusChip tone="success" icon={<CheckIcon />}>
                            Active
                          </StatusChip>
                        )}
                      </span>
                      <span className="text-[12px] text-ink-muted">
                        {coach.email}
                        {coach.linkedAt ? ` · joined ${formatDateShort(coach.linkedAt)}` : ''}
                      </span>
                      <span className="text-[12px] text-ink-muted" data-testid={`crm-team-coach-stats-${coach.coachId}`}>
                        {coachStats(coach)}
                      </span>
                    </span>
                    <Button
                      size="sm"
                      variant="ghost"
                      disabled={busy === coach.coachId}
                      data-testid={`crm-team-coach-remove-${coach.coachId}`}
                      onClick={() => void remove(coach)}
                    >
                      <TrashIcon /> Remove
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-[12px] text-ink-muted">
              Removing a coach hands their clients back to you. Their programmes and history stay with the
              client, and you can reassign from any client page.
            </p>
          </Panel>
        </>
      )}
    </div>
  );
}
